import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { DURATION, EASE_OUT_SOFT } from "../lib/motion";

/**
 * Beyond Sure — Cookie Consent (v3.4).
 *
 * Tasteful, on-brand glassmorphic consent strip pinned to the bottom of the
 * viewport on first visit. Does NOT block the form — sits above it on a
 * dedicated z-layer with backdrop-blur. Three buttons:
 *   • Accept all                  → save "accept"      → dismiss
 *   • Reject non-essential        → save "essential"   → dismiss
 *   • Manage                      → expand toggles     → save granular
 *
 * Persistence: localStorage key `bsure_cookie_consent_v1`. Shape:
 *   { choice: "accept"|"essential"|"custom",
 *     categories: { essential: true, analytics: bool, marketing: bool },
 *     savedAt: ISO date string }
 *
 * Once a choice is saved the popup never re-appears for that browser. The
 * essential cookies category is always true (login, CSRF, fraud) and is
 * shown as a disabled toggle in Manage mode to explain why.
 *
 * Real Beyond Sure: when the backend exposes /api/consent, the saved choice
 * should ALSO POST there on a debounce — but for the design sandbox the
 * localStorage record is enough. TODO: POST /api/consent.
 *
 * Accessibility: role="region", aria-label, focus-trap not needed (popup
 * doesn't capture focus), but tab order is respected and the buttons are
 * native <button> elements.
 */

const STORAGE_KEY = "bsure_cookie_consent_v1";

interface ConsentRecord {
  choice: "accept" | "essential" | "custom";
  categories: {
    essential: true;
    analytics: boolean;
    marketing: boolean;
  };
  savedAt: string;
}

function readStoredConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    return parsed;
  } catch {
    return null;
  }
}

function persistConsent(record: ConsentRecord) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Quota or disabled — fall through silently. The popup will reappear
    // next session; better than blocking the form.
  }
}

export function CookieConsent() {
  // Start hidden so SSR/first paint never flashes. The effect below decides.
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readStoredConsent();
    if (!existing) {
      // Slight delay so the popup doesn't compete with the page entrance.
      const id = window.setTimeout(() => setVisible(true), 900);
      return () => window.clearTimeout(id);
    }
  }, []);

  function saveAndDismiss(record: ConsentRecord) {
    persistConsent(record);
    setVisible(false);
  }

  function onAcceptAll() {
    saveAndDismiss({
      choice: "accept",
      categories: { essential: true, analytics: true, marketing: true },
      savedAt: new Date().toISOString(),
    });
  }

  function onRejectNonEssential() {
    saveAndDismiss({
      choice: "essential",
      categories: { essential: true, analytics: false, marketing: false },
      savedAt: new Date().toISOString(),
    });
  }

  function onSaveCustom() {
    saveAndDismiss({
      choice: "custom",
      categories: { essential: true, analytics, marketing },
      savedAt: new Date().toISOString(),
    });
  }

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="consent"
          role="region"
          aria-label="Cookie preferences"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: DURATION.slow, ease: EASE_OUT_SOFT }}
          className={[
            "fixed inset-x-3 bottom-3 sm:inset-x-auto sm:left-1/2 sm:bottom-5",
            "sm:-translate-x-1/2 sm:max-w-[640px] sm:w-[calc(100%-2rem)]",
            "z-[60] bsure-glass-crisp",
            "rounded-[var(--radius-xl)] px-5 py-4 sm:px-6 sm:py-5",
            "shadow-[0_24px_60px_-28px_rgb(0_0_0_/_0.45)]",
          ].join(" ")}
        >
          <div className="flex flex-col gap-3">
            {/* Header row — eyebrow + concise body copy */}
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                style={{ backgroundColor: "var(--color-brand-soft)" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 2a10 10 0 1010 10 5 5 0 01-5-5 5 5 0 01-5-5z"
                    stroke="var(--color-brand-deep)"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle cx="9" cy="10" r="0.9" fill="var(--color-brand-deep)" />
                  <circle cx="14" cy="14" r="0.9" fill="var(--color-brand-deep)" />
                  <circle cx="11" cy="17" r="0.9" fill="var(--color-brand-deep)" />
                </svg>
              </span>
              <div className="flex-1 min-w-0">
                <div className="bsure-caps text-[10px] text-[var(--color-brand-deep)]">
                  Cookies
                </div>
                <p className="bsure-prose mt-0.5 text-[13px] leading-[1.5] text-[var(--color-ink-80)]">
                  We use cookies to keep you signed in, prevent fraud, and
                  understand how BeyondSure is used. You're in control —{" "}
                  <a
                    href="#privacy"
                    className="text-[var(--color-brand-deep)] underline underline-offset-2 hover:text-[var(--color-ink)] transition-colors"
                  >
                    read our policy
                  </a>
                  .
                </p>
              </div>
            </div>

            {/* Manage panel — collapsible category toggles */}
            <AnimatePresence initial={false}>
              {managing ? (
                <motion.div
                  key="manage"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.24, ease: EASE_OUT_SOFT }}
                  className="overflow-hidden"
                >
                  <div className="bsure-ui mt-1 flex flex-col gap-2 rounded-[var(--radius-md)] p-3 bg-[var(--color-ink-04)]">
                    <CategoryToggle
                      label="Essential"
                      hint="Login, security, fraud prevention. Always on."
                      checked
                      disabled
                      onChange={() => {}}
                    />
                    <CategoryToggle
                      label="Analytics"
                      hint="Anonymized usage so we can improve the advisor."
                      checked={analytics}
                      onChange={setAnalytics}
                    />
                    <CategoryToggle
                      label="Marketing"
                      hint="Helps us send relevant content. Off by default."
                      checked={marketing}
                      onChange={setMarketing}
                    />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {/* Action row — buttons stack on phone, row on tablet+ */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-2.5">
              {managing ? (
                <>
                  <button
                    type="button"
                    onClick={() => setManaging(false)}
                    className="bsure-ui text-[12.5px] py-2 px-3 text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors sm:order-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={onSaveCustom}
                    className={[
                      "bsure-ui font-semibold text-[13px]",
                      "rounded-[var(--radius-pill)] py-2 px-4",
                      "bg-[var(--color-ink)] text-[var(--color-paper)]",
                      "transition-transform hover:scale-[1.02] active:scale-[0.98]",
                      "sm:order-2",
                    ].join(" ")}
                  >
                    Save preferences
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setManaging(true)}
                    className="bsure-ui text-[12.5px] py-2 px-3 text-[var(--color-ink-60)] hover:text-[var(--color-ink)] transition-colors sm:order-1"
                  >
                    Manage
                  </button>
                  <button
                    type="button"
                    onClick={onRejectNonEssential}
                    className={[
                      "bsure-ui font-medium text-[12.5px]",
                      "rounded-[var(--radius-pill)] py-2 px-3.5",
                      "border border-[var(--color-ink-16)] text-[var(--color-ink)]",
                      "hover:border-[var(--color-ink)] transition-colors",
                      "sm:order-2",
                    ].join(" ")}
                  >
                    Reject non-essential
                  </button>
                  <button
                    type="button"
                    onClick={onAcceptAll}
                    className={[
                      "bsure-ui font-semibold text-[13px]",
                      "rounded-[var(--radius-pill)] py-2 px-4",
                      "text-[var(--color-paper)]",
                      "transition-transform hover:scale-[1.02] active:scale-[0.98]",
                      "sm:order-3",
                    ].join(" ")}
                    style={{ backgroundColor: "var(--color-brand-deep)" }}
                  >
                    Accept all
                  </button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Internal — a single category toggle (Essential / Analytics / Marketing).
 * Inline component so the consent popup stays one file.
 */
function CategoryToggle({
  label,
  hint,
  checked,
  disabled = false,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      className={[
        "flex items-start gap-3 cursor-pointer select-none",
        disabled ? "opacity-70 cursor-not-allowed" : "",
      ].join(" ")}
    >
      <span
        role="switch"
        aria-checked={checked}
        className={[
          "mt-0.5 inline-flex h-[20px] w-[34px] shrink-0 items-center rounded-full p-0.5",
          "transition-colors duration-[var(--duration-fast)]",
          checked ? "" : "bg-[var(--color-ink-16)]",
        ].join(" ")}
        style={checked ? { backgroundColor: "var(--color-brand-deep)" } : undefined}
      >
        <span
          className={[
            "block h-[16px] w-[16px] rounded-full bg-[var(--color-paper)] shadow",
            "transition-transform duration-[var(--duration-fast)] ease-[var(--ease-standard)]",
            checked ? "translate-x-[14px]" : "translate-x-0",
          ].join(" ")}
        />
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="flex flex-col">
        <span className="bsure-ui text-[12.5px] font-semibold text-[var(--color-ink)]">
          {label}
        </span>
        <span className="bsure-prose text-[11.5px] leading-[1.4] text-[var(--color-ink-60)]">
          {hint}
        </span>
      </span>
    </label>
  );
}

export default CookieConsent;
